import type { CSSProperties } from 'react';
import { Image } from './Image';
import { ImageBlockFormData, IMAGE_VARIANT_NONE } from './contracts';

export type ImageBlockProps = ImageBlockFormData & {
  className?: string;
};

/**
 * Resolves the inline sizing for the `none` variant. Width and height are passed through as
 * CSS lengths exactly as the editor typed them: `320px` pins a size, and `100%` resolves
 * against the CMS column — which for height, in an auto-height column, is the browser's own
 * "grow to the image's natural height".
 *
 * `preserveAspectRatio` picks the fit: `contain` keeps the whole image inside the box at its
 * own proportions, `fill` stretches it to the box.
 */
const customSizing = (
  width: string | undefined,
  height: string | undefined,
  preserveAspectRatio: boolean
): CSSProperties => ({
  width: width || undefined,
  height: height || undefined,
  objectFit: preserveAspectRatio ? 'contain' : 'fill',
});

/**
 * CMS wrapper for the shared `Image`. An editor picks an asset from the media library (or
 * uploads a one-time image) and
 * either a named variant — which asks the assets proxy for that preset size and carries the
 * intrinsic dimensions that keep the page from shifting — or `none`, which hands sizing over
 * to the width/height/aspect-ratio configuration.
 *
 * Renders nothing without a chosen image: a bare `<img>` with no src re-requests the page
 * itself as an image (SC-1167), and an empty box on the live site helps nobody.
 */
export function ImageBlock({ className, image, variant, preserveAspectRatio = true, width, height }: ImageBlockProps) {
  if (!image?.src) {
    return null;
  }

  const isCustomSized = variant === IMAGE_VARIANT_NONE;

  return (
    <Image
      className={['image-block', className].filter(Boolean).join(' ')}
      src={image.src}
      alt={image.alt ?? ''}
      variant={isCustomSized ? undefined : variant}
      style={isCustomSized ? customSizing(width, height, preserveAspectRatio) : undefined}
    />
  );
}
